import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import {
  DollarSign, TrendingUp, Users, CheckCircle, Clock, Loader2,
  ChevronDown, ChevronUp, ExternalLink, Filter, ArrowUpRight,
  Banknote, AlertCircle, Receipt, RefreshCw, CloudDownload, Key
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || '';

const StatCard = ({ label, value, icon: Icon, color = 'emerald', sub }) => (
  <div className="bg-zinc-900/80 border border-zinc-800 rounded-xl p-5">
    <div className="flex items-center justify-between mb-2">
      <span className="text-zinc-400 text-sm">{label}</span>
      <div className={`p-2 rounded-lg bg-${color}-500/10`}>
        <Icon className={`h-4 w-4 text-${color}-400`} />
      </div>
    </div>
    <p className="text-2xl font-bold text-white">{value}</p>
    {sub && <p className="text-xs text-zinc-500 mt-1">{sub}</p>}
  </div>
);

const CreatorEarningsTab = ({ authToken }) => {
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [selectedCreator, setSelectedCreator] = useState(null);
  const [expandedCreator, setExpandedCreator] = useState(null);
  const [creatorTxns, setCreatorTxns] = useState([]);
  const [loadingTxns, setLoadingTxns] = useState(false);
  const [markingPaid, setMarkingPaid] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [error, setError] = useState(null);
  const [syncingCreator, setSyncingCreator] = useState(null);
  const [syncResult, setSyncResult] = useState(null);

  const headers = { Authorization: `Bearer ${authToken}` };

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/api/admin/creator-earnings/summary`, { headers });
      setSummary(res.data);
    } catch (err) {
      console.error('Failed to fetch earnings summary:', err);
      setError('Failed to load earnings data');
    } finally {
      setLoading(false);
    }
  }, [authToken]);

  useEffect(() => {
    if (authToken) fetchSummary();
  }, [authToken, fetchSummary]);

  const fetchCreatorTransactions = async (creatorId) => {
    setLoadingTxns(true);
    try {
      const params = new URLSearchParams({ creator_id: creatorId, limit: '100' });
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (typeFilter !== 'all') params.append('type', typeFilter);
      const res = await axios.get(`${API}/api/admin/creator-earnings/transactions?${params}`, { headers });
      setCreatorTxns(res.data.transactions || []);
    } catch (err) {
      console.error('Failed to fetch transactions:', err);
    } finally {
      setLoadingTxns(false);
    }
  };

  const toggleCreatorExpand = (creatorId) => {
    if (expandedCreator === creatorId) {
      setExpandedCreator(null);
      setCreatorTxns([]);
    } else {
      setExpandedCreator(creatorId);
      fetchCreatorTransactions(creatorId);
    }
  };

  useEffect(() => {
    if (expandedCreator) fetchCreatorTransactions(expandedCreator);
  }, [statusFilter, typeFilter]);

  const handleMarkAllPaid = async (creatorId, creatorName) => {
    if (!window.confirm(`Mark all pending fees as collected for ${creatorName}?`)) return;
    setMarkingPaid(creatorId);
    try {
      await axios.post(
        `${API}/api/admin/creator-earnings/mark-paid?creator_id=${creatorId}`,
        {},
        { headers }
      );
      await fetchSummary();
      if (expandedCreator === creatorId) fetchCreatorTransactions(creatorId);
    } catch (err) {
      console.error('Failed to mark as paid:', err);
      alert('Failed to mark as paid. Please try again.');
    } finally {
      setMarkingPaid(null);
    }
  };

  const handleMarkSinglePaid = async (earningId) => {
    setMarkingPaid(earningId);
    try {
      await axios.post(
        `${API}/api/admin/creator-earnings/mark-transaction-paid/${earningId}`,
        {},
        { headers }
      );
      if (expandedCreator) fetchCreatorTransactions(expandedCreator);
      await fetchSummary();
    } catch (err) {
      console.error('Failed to mark transaction:', err);
      alert('Failed to mark transaction as paid.');
    } finally {
      setMarkingPaid(null);
    }
  };

  const handleSyncFromStripe = async (creatorId, creatorName, reset = false) => {
    if (reset && !window.confirm(`Reset & re-sync all unpaid transactions for ${creatorName}? This will re-classify all transactions correctly.`)) {
      return;
    }
    setSyncingCreator(creatorId);
    setSyncResult(null);
    try {
      const url = `${API}/api/admin/creator-earnings/sync-from-stripe/${creatorId}${reset ? '?reset=true' : ''}`;
      const res = await axios.post(url, {}, { headers });
      const data = res.data;
      setSyncResult({
        creatorId,
        success: true,
        message: data.message || `Synced ${data.synced} transactions`
      });
      // Refresh data
      await fetchSummary();
      if (expandedCreator === creatorId) fetchCreatorTransactions(creatorId);
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || 'Sync failed unexpectedly.';
      setSyncResult({ creatorId, success: false, message: detail });
    } finally {
      setSyncingCreator(null);
      setTimeout(() => setSyncResult(null), 8000);
    }
  };

  const fmtCurrency = (amount) => `£${(amount || 0).toFixed(2)}`;
  const fmtDate = (iso) => {
    if (!iso) return '-';
    try {
      return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch { return iso; }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="creator-earnings-loading">
        <Loader2 className="h-8 w-8 animate-spin text-zinc-400" />
        <span className="ml-3 text-zinc-400">Loading earnings data...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center" data-testid="creator-earnings-error">
        <AlertCircle className="h-10 w-10 text-red-400 mb-3" />
        <p className="text-red-400 mb-4">{error}</p>
        <Button variant="outline" onClick={fetchSummary} className="border-zinc-700 text-zinc-300 hover:bg-zinc-800">
          Try Again
        </Button>
      </div>
    );
  }

  const totals = summary?.totals || {};
  const creators = summary?.creators || [];

  return (
    <div className="space-y-6" data-testid="creator-earnings-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-white">Creator Earnings</h2>
          <p className="text-sm text-zinc-400 mt-1">
            Payments go directly to creators. Platform fees shown here are what creators owe you.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={fetchSummary}
          className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
          data-testid="refresh-earnings-btn"
        >
          <ArrowUpRight className="h-4 w-4 mr-1" /> Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="earnings-summary-cards">
        <StatCard
          label="Total Revenue"
          value={fmtCurrency(totals.total_revenue)}
          icon={DollarSign}
          color="emerald"
          sub="All creator transactions"
        />
        <StatCard
          label="Owed to Platform"
          value={fmtCurrency(totals.total_owed_to_platform)}
          icon={Banknote}
          color="blue"
          sub="Total platform fees"
        />
        <StatCard
          label="Pending Collection"
          value={fmtCurrency(totals.pending_collection)}
          icon={Clock}
          color="amber"
          sub="Fees not yet collected"
        />
        <StatCard
          label="Collected"
          value={fmtCurrency(totals.collected)}
          icon={CheckCircle}
          color="emerald"
          sub="Already collected"
        />
      </div>

      {/* Creator List */}
      {creators.length === 0 ? (
        <div className="bg-zinc-900/80 border border-zinc-800 rounded-xl p-12 text-center" data-testid="no-earnings">
          <Receipt className="h-12 w-12 text-zinc-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-zinc-300 mb-2">No creators found</h3>
          <p className="text-zinc-500 text-sm">Create creator channels first, then sync their Stripe data here.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="creator-earnings-list">
          {creators.map((creator) => (
            <div key={creator.creator_id} className="bg-zinc-900/80 border border-zinc-800 rounded-xl overflow-hidden">
              {/* Creator Row */}
              <div
                className="flex items-center justify-between p-4 cursor-pointer hover:bg-zinc-800/50 transition-colors"
                onClick={() => toggleCreatorExpand(creator.creator_id)}
                data-testid={`creator-row-${creator.creator_id}`}
              >
                <div className="flex items-center gap-4 flex-1 min-w-0">
                  <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold text-sm">
                      {(creator.creator_name || '?')[0].toUpperCase()}
                    </span>
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="text-white font-medium truncate">{creator.creator_name}</h3>
                      {creator.has_stripe_key ? (
                        <span className="flex items-center gap-0.5 text-[10px] text-emerald-400 bg-emerald-500/10 px-1.5 py-0.5 rounded-full" title="Stripe key configured">
                          <Key className="h-2.5 w-2.5" /> Stripe
                        </span>
                      ) : (
                        <span className="flex items-center gap-0.5 text-[10px] text-zinc-500 bg-zinc-800 px-1.5 py-0.5 rounded-full" title="No Stripe key">
                          <Key className="h-2.5 w-2.5" /> No key
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-zinc-500">{creator.transactions} transaction{creator.transactions !== 1 ? 's' : ''}{creator.platform_fee_pct > 0 ? ` · ${creator.platform_fee_pct}% fee` : ''}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3 text-sm">
                  <div className="text-right hidden sm:block">
                    <p className="text-zinc-400">Revenue</p>
                    <p className="text-white font-medium">{fmtCurrency(creator.total_revenue)}</p>
                  </div>
                  <div className="text-right hidden md:block">
                    <p className="text-zinc-400">Creator Kept</p>
                    <p className="text-emerald-400 font-medium">{fmtCurrency(creator.creator_kept)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-zinc-400">Pending</p>
                    <p className={`font-medium ${creator.pending_collection > 0 ? 'text-amber-400' : 'text-zinc-500'}`}>
                      {fmtCurrency(creator.pending_collection)}
                    </p>
                  </div>
                  <div className="text-right hidden sm:block">
                    <p className="text-zinc-400">Collected</p>
                    <p className="text-emerald-400 font-medium">{fmtCurrency(creator.collected)}</p>
                  </div>

                  {/* Sync from Stripe buttons */}
                  {creator.has_stripe_key && (
                    <div className="flex items-center gap-1">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => { e.stopPropagation(); handleSyncFromStripe(creator.creator_id, creator.creator_name); }}
                        disabled={syncingCreator === creator.creator_id}
                        className="border-blue-600/40 text-blue-400 hover:bg-blue-600/10 hover:text-blue-300 text-xs whitespace-nowrap"
                        data-testid={`sync-stripe-${creator.creator_id}`}
                      >
                        {syncingCreator === creator.creator_id
                          ? <><Loader2 className="h-3 w-3 animate-spin mr-1" /> Syncing...</>
                          : <><CloudDownload className="h-3 w-3 mr-1" /> Sync</>
                        }
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => { e.stopPropagation(); handleSyncFromStripe(creator.creator_id, creator.creator_name, true); }}
                        disabled={syncingCreator === creator.creator_id}
                        className="border-amber-600/40 text-amber-400 hover:bg-amber-600/10 hover:text-amber-300 text-xs whitespace-nowrap"
                        data-testid={`reset-sync-${creator.creator_id}`}
                        title="Clear all unpaid records and re-sync from scratch — fixes misclassified transactions"
                      >
                        <RefreshCw className="h-3 w-3" />
                      </Button>
                    </div>
                  )}

                  {creator.pending_collection > 0 && (
                    <Button
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); handleMarkAllPaid(creator.creator_id, creator.creator_name); }}
                      disabled={markingPaid === creator.creator_id}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs whitespace-nowrap"
                      data-testid={`mark-all-paid-${creator.creator_id}`}
                    >
                      {markingPaid === creator.creator_id
                        ? <Loader2 className="h-3 w-3 animate-spin" />
                        : <><CheckCircle className="h-3 w-3 mr-1" /> Collect All</>
                      }
                    </Button>
                  )}

                  {expandedCreator === creator.creator_id
                    ? <ChevronUp className="h-5 w-5 text-zinc-500" />
                    : <ChevronDown className="h-5 w-5 text-zinc-500" />
                  }
                </div>
              </div>

              {/* Sync result toast */}
              {syncResult && syncResult.creatorId === creator.creator_id && (
                <div className={`mx-4 mb-3 px-4 py-2.5 rounded-lg text-sm flex items-center gap-2 ${
                  syncResult.success
                    ? 'bg-emerald-500/10 border border-emerald-500/30 text-emerald-400'
                    : 'bg-red-500/10 border border-red-500/30 text-red-400'
                }`} data-testid={`sync-result-${creator.creator_id}`}>
                  {syncResult.success ? <CheckCircle className="h-4 w-4 flex-shrink-0" /> : <AlertCircle className="h-4 w-4 flex-shrink-0" />}
                  {syncResult.message}
                </div>
              )}

              {/* Expanded Transaction List */}
              {expandedCreator === creator.creator_id && (
                <div className="border-t border-zinc-800" data-testid={`creator-txns-${creator.creator_id}`}>
                  {/* Filters */}
                  <div className="flex flex-wrap items-center gap-2 px-4 py-3 bg-zinc-950/50">
                    <Filter className="h-4 w-4 text-zinc-500" />
                    {/* Type filter */}
                    {[
                      { key: 'all', label: 'All Types' },
                      { key: 'vod_purchase', label: 'VOD' },
                      { key: 'livestream_ticket', label: 'LTV Live' },
                      { key: 'stripe_sync', label: 'Synced' }
                    ].map((f) => (
                      <button
                        key={f.key}
                        onClick={() => setTypeFilter(f.key)}
                        className={`px-3 py-1 text-xs rounded-full transition-colors ${
                          typeFilter === f.key
                            ? f.key === 'vod_purchase' ? 'bg-blue-600/30 text-blue-400'
                              : f.key === 'livestream_ticket' ? 'bg-purple-600/30 text-purple-400'
                              : 'bg-zinc-700 text-white'
                            : 'text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800'
                        }`}
                        data-testid={`filter-type-${f.key}`}
                      >
                        {f.label}
                      </button>
                    ))}
                    <span className="text-zinc-700 mx-1">|</span>
                    {/* Status filter */}
                    {['all', 'pending', 'paid'].map((f) => (
                      <button
                        key={f}
                        onClick={() => setStatusFilter(f)}
                        className={`px-3 py-1 text-xs rounded-full transition-colors ${
                          statusFilter === f
                            ? 'bg-zinc-700 text-white'
                            : 'text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800'
                        }`}
                        data-testid={`filter-${f}`}
                      >
                        {f.charAt(0).toUpperCase() + f.slice(1)}
                      </button>
                    ))}
                  </div>

                  {loadingTxns ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-5 w-5 animate-spin text-zinc-500" />
                    </div>
                  ) : creatorTxns.length === 0 ? (
                    <div className="text-center py-8 text-zinc-500 text-sm">
                      No {typeFilter !== 'all' ? (typeFilter === 'livestream_ticket' ? 'LTV Live' : typeFilter === 'vod_purchase' ? 'VOD' : '') : ''} {statusFilter !== 'all' ? statusFilter : ''} transactions found
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-zinc-500 text-xs border-b border-zinc-800">
                            <th className="text-left px-4 py-2 font-medium">Date</th>
                            <th className="text-left px-4 py-2 font-medium">Description</th>
                            <th className="text-left px-4 py-2 font-medium">Customer</th>
                            <th className="text-right px-4 py-2 font-medium">Amount</th>
                            <th className="text-right px-4 py-2 font-medium">Platform Fee</th>
                            <th className="text-center px-4 py-2 font-medium">Status</th>
                            <th className="text-right px-4 py-2 font-medium">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {creatorTxns.map((txn) => (
                            <tr key={txn.id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30">
                              <td className="px-4 py-3 text-zinc-400 whitespace-nowrap">{fmtDate(txn.created_at)}</td>
                              <td className="px-4 py-3">
                                <span className="text-white">{txn.description || txn.video_title || 'Transaction'}</span>
                                <span className={`ml-2 text-xs px-1.5 py-0.5 rounded ${
                                  txn.type === 'livestream_ticket'
                                    ? 'bg-purple-500/20 text-purple-400'
                                    : txn.type === 'vod_purchase'
                                    ? 'bg-blue-500/20 text-blue-400'
                                    : 'bg-zinc-700/50 text-zinc-400'
                                }`}>
                                  {txn.type === 'livestream_ticket' ? 'LTV Live' : txn.type === 'vod_purchase' ? 'VOD' : 'Other'}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-zinc-400 truncate max-w-[180px]">{txn.customer_email || '-'}</td>
                              <td className="px-4 py-3 text-right text-white font-medium">{fmtCurrency(txn.total_amount)}</td>
                              <td className="px-4 py-3 text-right text-amber-400">{fmtCurrency(txn.platform_fee_amount)}</td>
                              <td className="px-4 py-3 text-center">
                                {txn.status === 'paid' ? (
                                  <span className="inline-flex items-center gap-1 text-xs text-emerald-400 bg-emerald-500/10 px-2 py-1 rounded-full">
                                    <CheckCircle className="h-3 w-3" /> Collected
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1 text-xs text-amber-400 bg-amber-500/10 px-2 py-1 rounded-full">
                                    <Clock className="h-3 w-3" /> Pending
                                  </span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-right">
                                {txn.status === 'pending' && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleMarkSinglePaid(txn.id)}
                                    disabled={markingPaid === txn.id}
                                    className="text-xs text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10 h-7"
                                    data-testid={`mark-paid-${txn.id}`}
                                  >
                                    {markingPaid === txn.id
                                      ? <Loader2 className="h-3 w-3 animate-spin" />
                                      : 'Collect'
                                    }
                                  </Button>
                                )}
                                {txn.status === 'paid' && txn.paid_at && (
                                  <span className="text-xs text-zinc-600">{fmtDate(txn.paid_at)}</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CreatorEarningsTab;
